import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableSortLabel, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, MenuItem, Grow, Chip, Select, Autocomplete, InputAdornment, Divider, Card, ToggleButton, ToggleButtonGroup, Grid, Tooltip, Checkbox, FormControlLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import API_URL from '../config';

const MyTasks = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('General Tasks');
    const [statusFilter, setStatusFilter] = useState('Open');
    const [assignedToFilter, setAssignedToFilter] = useState(null);
    const [createdByFilter, setCreatedByFilter] = useState(null);
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentTask, setCurrentTask] = useState({ description: '', status: 'Pending', due_date: '', assigned_to: '' });
    const [draggedTaskId, setDraggedTaskId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'order_index', direction: 'asc' });

    const fetchUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/team`, { headers: { Authorization: `Bearer ${token}` }});
            setUsers(res.data);
            
            // Getting current user from localStorage if saved, or decode token payload
            const storedUser = localStorage.getItem('username'); // checking if username is stored
            if (storedUser) {
                setCurrentUser(storedUser);
            } else {
                try {
                    if (token) {
                        const tokenParts = token.split('.');
                        if (tokenParts.length === 3) {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            // 'sub' is usually the username or the user ID depending on JWT setup
                            setCurrentUser(payload.sub);
                        }
                    }
                } catch (e) {
                    console.error("Could not parse token to determine user ID", e);
                }
            }
            
        } catch (error) {
            console.error("Error fetching users", error);
        }
    }, []);

    const fetchTasks = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/api/tasks`, { headers: { Authorization: `Bearer ${token}` }});
            setTasks(res.data);
        } catch (error) {
            console.error("Error fetching tasks", error);
        }
    }, []);

    useEffect(() => {
        fetchTasks();
        fetchUsers();
    }, [fetchTasks, fetchUsers]);

    const handleOpen = (task = null) => {
        let defaultUser = '';
        if (currentUser && users.length > 0) {
            const match = users.find(u => String(u.id) === String(currentUser) || u.name === currentUser || u.username === currentUser);
            if(match) defaultUser = match.id || match.name; 
        }
        if(!defaultUser && currentUser) {
            defaultUser = currentUser; 
        }

        if (task) {
            setEditMode(true);
            setCurrentTask({ ...task, subtasks: task.subtasks || [] });
        } else {
            setEditMode(false);
            setCurrentTask({ 
                name: '',
                description: '', 
                category: 'General',
                priority: 'Medium',
                start_date: '',
                due_date: '', 
                estimated_hours: '',
                status: 'Pending', 
                assigned_to: defaultUser || '',
                subtasks: []
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            if (editMode) {
                // If it's a standalone task, update endpoint doesn't need to change much, just route PUT `/tasks/actualId`
                // Ensure proper prefix formatting for standalone task
                const putId = currentTask.id || `task_${currentTask.actual_id}`; 
                await axios.put(`${API_URL}/api/tasks/${putId}`, currentTask, { headers: { Authorization: `Bearer ${token}` }});
            } else {
                await axios.post(`${API_URL}/api/tasks`, currentTask, { headers: { Authorization: `Bearer ${token}` }});
            }
            fetchTasks();
            handleClose();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this task?")) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/api/tasks/${id}`, { headers: { Authorization: `Bearer ${token}` }});
                fetchTasks();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const getStatusColor = (status) => {
        switch(status?.toLowerCase()) {
            case 'completed': return 'success';
            case 'done': return 'success';
            case 'closed': return 'default';
            case 'in progress': return 'primary';
            case 'open': return 'primary';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const getStatusOptions = (source) => {
        switch(source) {
            case 'ticket':
                return ['Open', 'In Progress', 'Done', 'Closed'];
            case 'it_project_task':
            case 'schedule':
            case 'lead_followup':
            case 'client_followup':
                return ['Pending', 'Completed'];
            default:
                // Standalone task
                return ['Pending', 'In Progress', 'Completed'];
        }
    };

    const handleRowClick = (t) => {
        if (t.source === 'ticket') navigate(`/tickets/${t.actual_id}`);
        else if (t.source === 'it_project_task' && t.project_id) navigate(`/it-projects/${t.project_id}`);
        else if (t.source === 'client_followup' && t.related_to_id) navigate(`/clients/${t.related_to_id}`);
        else if (t.source === 'lead_followup' && t.related_to_id) navigate(`/leads/${t.related_to_id}`);
        else if (t.source === 'task' && t.related_to_type === 'project' && t.project_id) navigate(`/projects/${t.project_id}`);
        else if (t.source === 'schedule' && t.project_id) navigate(`/projects/${t.project_id}`);
    };

    const handleInlineStatusChange = async (t, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/tasks/${t.id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` }});
            fetchTasks();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDragStart = (e, id) => {
        setDraggedTaskId(id);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e, droppedOnId) => {
        e.preventDefault();
        if (!draggedTaskId || draggedTaskId === droppedOnId) return;

        // Current visible list
        const list = [...filteredTasks];
        const draggedIndex = list.findIndex(t => (t.id || t.actual_id) === draggedTaskId);
        const droppedIndex = list.findIndex(t => (t.id || t.actual_id) === droppedOnId);

        if (draggedIndex === -1 || droppedIndex === -1) return;

        const item = list.splice(draggedIndex, 1)[0];
        list.splice(droppedIndex, 0, item);

        const ordersPayload = list.map((t, index) => ({ id: t.id, order_index: index }));

        // Optimistically update
        const newTasks = tasks.map(t => {
            const updateItem = ordersPayload.find(o => o.id === t.id);
            if (updateItem) return { ...t, order_index: updateItem.order_index };
            return t;
        });
        setTasks(newTasks);
        setSortConfig({ key: 'order_index', direction: 'asc' });

        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/tasks/order`, { orders: ordersPayload }, { headers: { Authorization: `Bearer ${token}` }});
        } catch (error) {
            console.error(error);
        }
        setDraggedTaskId(null);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const filteredTasks = useMemo(() => {
        const filtered = tasks.filter(t => {
            // filter by search
            const matchSearch = String(t.name || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.main_record_name || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.description || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.status || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.priority || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.start_date || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.due_date || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.assigned_to_name || '').toLowerCase().includes(search.toLowerCase()) ||
                                String(t.created_by_name || '').toLowerCase().includes(search.toLowerCase());
            
            // filter by type
            let matchFilter = true;
            if (filter !== 'All') {
                if (filter === 'Tickets' && t.source !== 'ticket') matchFilter = false;
                else if (filter === 'IT Projects' && t.source !== 'it_project_task') matchFilter = false;
                else if (filter === 'General Tasks' && t.source !== 'task') matchFilter = false;
                else if (filter === 'Follow-ups' && !['lead_followup', 'client_followup'].includes(t.source)) matchFilter = false;
                else if (filter === 'Schedules' && t.source !== 'schedule') matchFilter = false;
            }

            // filter by status (Open/Closed/All)
            let matchStatus = true;
            const s = String(t.status || t.main_record_status || '').toLowerCase();
            const isClosed = ['completed', 'done', 'closed'].includes(s);
            if (statusFilter === 'Open' && isClosed) matchStatus = false;
            if (statusFilter === 'Closed' && !isClosed) matchStatus = false;

            // filter by assigned to
            const matchAssigned = !assignedToFilter || String(t.assigned_to_name || '').toLowerCase().includes((assignedToFilter.full_name || assignedToFilter.name || '').toLowerCase());

            // filter by created by
            const matchCreatedBy = !createdByFilter || String(t.created_by_name || '').toLowerCase().includes((createdByFilter.full_name || createdByFilter.name || '').toLowerCase());

            return matchSearch && matchFilter && matchStatus && matchAssigned && matchCreatedBy;
        });

        filtered.sort((a, b) => {
            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];

            // Specific handling for Item/Project which has fallbacks in rendering
            if (sortConfig.key === 'main_record_name') {
                valA = a.main_record_name || a.project_name || a.source || '';
                valB = b.main_record_name || b.project_name || b.source || '';
            }

            valA = valA !== undefined && valA !== null ? valA : '';
            valB = valB !== undefined && valB !== null ? valB : '';
            
            if (typeof valA === 'number' && typeof valB === 'number') {
                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            }
            
            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [tasks, search, filter, statusFilter, assignedToFilter, createdByFilter, sortConfig]);

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            
            {/* Modern Gradient Header */}
            <Grow in={true} timeout={500}>
                <Box sx={{ 
                    mb: 4, 
                    mt: 2, 
                    p: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 3
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <AssignmentIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            My Tasks
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Manage and track all your assigned tasks
                        </Typography>
                    </Box>
                    
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => handleOpen()}
                        sx={{ 
                            position: 'relative',
                            zIndex: 2,
                            bgcolor: 'rgba(255,255,255,0.1)', 
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            textTransform: 'none',
                            px: 3,
                            py: 1.5,
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.2)',
                                transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Create Task
                    </Button>
                    
                    {/* Decorative abstract shapes */}
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)', zIndex: 1 }} />
                    <Box sx={{ position: 'absolute', bottom: -100, left: 150, width: 300, height: 300, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)', zIndex: 1 }} />
                </Box>
            </Grow>
            
            {/* Filter and Search Bar */}
            <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                {/* Row 1: Type filters */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                    <Chip
                        icon={<TaskAltIcon />}
                        label="General Tasks"
                        onClick={() => setFilter(filter === 'General Tasks' ? 'All' : 'General Tasks')}
                        sx={{ 
                            fontWeight: 'bold', 
                            borderRadius: 2, 
                            px: 1,
                            bgcolor: filter === 'General Tasks' ? '#eff6ff' : '#f8fafc',
                            color: filter === 'General Tasks' ? '#2563eb' : '#64748b',
                            border: '1px solid',
                            borderColor: filter === 'General Tasks' ? '#bfdbfe' : '#e2e8f0',
                            '&:hover': { bgcolor: filter === 'General Tasks' ? '#dbeafe' : '#f1f5f9' },
                        }}
                    />
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: 'center' }} />
                    {['All', 'Tickets', 'IT Projects', 'Follow-ups', 'Schedules'].map(f => (
                        <Chip 
                            key={f} 
                            label={f} 
                            onClick={() => setFilter(f)} 
                            sx={{ 
                                fontWeight: '600', 
                                borderRadius: 2,
                                bgcolor: filter === f ? '#1e293b' : 'transparent',
                                color: filter === f ? '#ffffff' : '#64748b',
                                border: '1px solid',
                                borderColor: filter === f ? '#1e293b' : 'transparent',
                                '&:hover': { bgcolor: filter === f ? '#0f172a' : '#f1f5f9' },
                            }}
                        />
                    ))}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {/* Row 2: Search + status + user filters */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField 
                        placeholder="Search tasks..." 
                        size="small" 
                        value={search} 
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                        }}
                        sx={{ 
                            flexGrow: 1,
                            minWidth: 200,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f8fafc',
                                borderRadius: 2,
                                '& fieldset': { borderColor: '#e2e8f0' },
                                '&:hover fieldset': { borderColor: '#cbd5e1' },
                                '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                            }
                        }}
                    />
                    <ToggleButtonGroup
                        value={statusFilter}
                        exclusive
                        onChange={(e, newStatus) => { if (newStatus) setStatusFilter(newStatus); }}
                        size="small"
                        sx={{ 
                            '& .MuiToggleButton-root': { 
                                color: '#64748b',
                                borderColor: '#e2e8f0',
                                textTransform: 'none',
                                fontWeight: '600',
                                px: 2,
                                height: 40,
                                '&.Mui-selected': { bgcolor: '#eff6ff', color: '#3b82f6', borderColor: '#bfdbfe' },
                                '&:hover': { bgcolor: '#f8fafc' }
                            }
                        }}
                    >
                        <ToggleButton value="Open">Open</ToggleButton>
                        <ToggleButton value="Closed">Closed</ToggleButton>
                        <ToggleButton value="All">All</ToggleButton>
                    </ToggleButtonGroup>
                    <Autocomplete
                        size="small"
                        options={users}
                        getOptionLabel={u => u.full_name || u.name || ''}
                        value={assignedToFilter}
                        onChange={(_, v) => setAssignedToFilter(v)}
                        sx={{ width: 180 }}
                        renderInput={params => <TextField {...params} placeholder="Assigned to..." sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', borderRadius: 2, '& fieldset': { borderColor: '#e2e8f0' } } }} />}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                    />
                    <Autocomplete
                        size="small"
                        options={users}
                        getOptionLabel={u => u.full_name || u.name || ''}
                        value={createdByFilter}
                        onChange={(_, v) => setCreatedByFilter(v)}
                        sx={{ width: 180 }}
                        renderInput={params => <TextField {...params} placeholder="Created by..." sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', borderRadius: 2, '& fieldset': { borderColor: '#e2e8f0' } } }} />}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                    />
                </Box>
            </Card>

            <Grow in={true} timeout={800}>
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'main_record_name'} direction={sortConfig.key === 'main_record_name' ? sortConfig.direction : 'asc'} onClick={() => handleSort('main_record_name')}>Item/Project</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'name'} direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'} onClick={() => handleSort('name')}>Task</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'description'} direction={sortConfig.key === 'description' ? sortConfig.direction : 'asc'} onClick={() => handleSort('description')}>Description</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'start_date'} direction={sortConfig.key === 'start_date' ? sortConfig.direction : 'asc'} onClick={() => handleSort('start_date')}>Start</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'due_date'} direction={sortConfig.key === 'due_date' ? sortConfig.direction : 'asc'} onClick={() => handleSort('due_date')}>Due</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'priority'} direction={sortConfig.key === 'priority' ? sortConfig.direction : 'asc'} onClick={() => handleSort('priority')}>Priority</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'assigned_to_name'} direction={sortConfig.key === 'assigned_to_name' ? sortConfig.direction : 'asc'} onClick={() => handleSort('assigned_to_name')}>Assigned</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                    <TableSortLabel active={sortConfig.key === 'created_by_name'} direction={sortConfig.key === 'created_by_name' ? sortConfig.direction : 'asc'} onClick={() => handleSort('created_by_name')}>Created</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', width: '150px' }}>
                                    <TableSortLabel active={sortConfig.key === 'status'} direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#64748b' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTasks.length > 0 ? filteredTasks.map(t => (
                                <TableRow 
                                    key={t.id || t.actual_id} 
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, t.id || t.actual_id)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, t.id || t.actual_id)}
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 }, 
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' },
                                        cursor: 'grab',
                                        opacity: draggedTaskId === (t.id || t.actual_id) ? 0.5 : 1
                                    }}
                                >
                                    <TableCell>
                                        <Typography 
                                            variant="subtitle2" 
                                            fontWeight="bold" 
                                            color="primary"
                                            onClick={() => handleRowClick(t)}
                                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        >
                                            {t.main_record_name || t.project_name || '-'}
                                        </Typography>
                                        <Chip 
                                            label={t.source ? t.source.replace(/_/g, ' ').toUpperCase() : 'TASK'} 
                                            size="small" 
                                            sx={{ mt: 0.5, fontSize: '0.65rem', height: '20px' }} 
                                        />
                                        {t.main_record_status && t.main_record_status !== 'N/A' && (
                                            <Chip 
                                                label={t.main_record_status} 
                                                size="small" 
                                                color={getStatusColor(t.main_record_status)}
                                                variant="outlined"
                                                sx={{ mt: 0.5, ml: 1, fontSize: '0.65rem', height: '20px' }} 
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="500">{t.name || '-'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        <Tooltip title={t.description || 'No description'} placement="top" arrow>
                                            <Typography variant="body2" fontWeight="500" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'help' }}>
                                                {t.description || '-'}
                                            </Typography>
                                        </Tooltip>
                                        <Typography variant="caption" color="text.secondary">Created: {t.created_date || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{t.start_date || '-'}</TableCell>
                                    <TableCell>{t.due_date || '-'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={t.priority || 'Medium'} 
                                            size="small" 
                                            color={t.priority === 'High' ? 'error' : t.priority === 'Low' ? 'success' : 'warning'}
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: '22px' }} 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: t.assigned_to_name ? '#0369a1' : '#94a3b8', fontWeight: 500 }}>
                                            {t.assigned_to_name || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: t.created_by_name ? '#6d28d9' : '#94a3b8', fontWeight: 500 }}>
                                            {t.created_by_name || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            value={t.status || 'Pending'}
                                            size="small"
                                            onChange={(e) => handleInlineStatusChange(t, e.target.value)}
                                            sx={{
                                                fontSize: '0.8rem',
                                                fontWeight: 'bold',
                                                height: '32px',
                                                bgcolor: getStatusColor(t.status) === 'success' ? '#e8f5e9' : getStatusColor(t.status) === 'warning' ? '#fff8e1' : '#e3f2fd',
                                                color: getStatusColor(t.status) === 'success' ? '#2e7d32' : getStatusColor(t.status) === 'warning' ? '#f57f17' : '#1565c0',
                                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                                            }}
                                        >
                                            {getStatusOptions(t.source).map(opt => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="right">
                                        {(!t.source || t.source === 'task') && (
                                            <>
                                                <IconButton onClick={() => handleOpen(t)} color="primary" size="small"><EditIcon fontSize="small" /></IconButton>
                                                <IconButton onClick={() => handleDelete(t.id || t.actual_id)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                        <Typography variant="body1" color="text.secondary">No tasks found.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grow>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {editMode ? 'Edit Task' : 'Create Task'}
                    <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        {/* Basic Information section */}
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Basic Information</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth label="Task Title" 
                                        value={currentTask.name || ''} 
                                        onChange={e => setCurrentTask({...currentTask, name: e.target.value})} 
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth label="Description" multiline rows={3} 
                                        value={currentTask.description || ''} 
                                        onChange={e => setCurrentTask({...currentTask, description: e.target.value})} 
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Details & Assignment section */}
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Details & Assignment</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={users || []}
                                        getOptionLabel={(option) => option.name || option.username || String(option)}
                                        value={
                                            users.find(u => u.id === currentTask.assigned_to || u.name === currentTask.assigned_to) || currentTask.assigned_to || null
                                        }
                                        onChange={(e, newValue) => {
                                            setCurrentTask({ ...currentTask, assigned_to: newValue ? (newValue.id || newValue.name) : '' });
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Assigned To" fullWidth />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField 
                                        fullWidth label="Category / Type" select
                                        value={currentTask.category || 'General'} 
                                        onChange={e => setCurrentTask({...currentTask, category: e.target.value})}
                                    >
                                        <MenuItem value="General">General</MenuItem>
                                        <MenuItem value="Bug">Bug</MenuItem>
                                        <MenuItem value="Feature">Feature Request</MenuItem>
                                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                                        <MenuItem value="Meeting">Meeting</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Timeline & Status section */}
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Timeline & Status</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField 
                                        fullWidth label="Start Date" type="date" InputLabelProps={{ shrink: true }} 
                                        value={currentTask.start_date ? currentTask.start_date.substring(0, 10) : ''} 
                                        onChange={e => setCurrentTask({...currentTask, start_date: e.target.value})} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField 
                                        fullWidth label="Due Date" type="date" InputLabelProps={{ shrink: true }} 
                                        value={currentTask.due_date ? currentTask.due_date.substring(0, 10) : ''} 
                                        onChange={e => setCurrentTask({...currentTask, due_date: e.target.value})} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField 
                                        fullWidth label="Priority" select
                                        value={currentTask.priority || 'Medium'} 
                                        onChange={e => setCurrentTask({...currentTask, priority: e.target.value})}
                                    >
                                        <MenuItem value="High">High</MenuItem>
                                        <MenuItem value="Medium">Medium</MenuItem>
                                        <MenuItem value="Low">Low</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField 
                                        fullWidth label="Est. Hours" type="number"
                                        value={currentTask.estimated_hours || ''} 
                                        onChange={e => setCurrentTask({...currentTask, estimated_hours: e.target.value})} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField 
                                        fullWidth label="Status" select
                                        value={currentTask.status || 'Pending'} 
                                        onChange={e => setCurrentTask({...currentTask, status: e.target.value})}
                                    >
                                        <MenuItem value="Pending">Pending</MenuItem>
                                        <MenuItem value="In Progress">In Progress</MenuItem>
                                        <MenuItem value="Completed">Completed</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Subtasks section */}
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Subtasks</Typography>
                                <Button 
                                    size="small" 
                                    startIcon={<AddIcon />} 
                                    onClick={() => {
                                        const newSubtasks = [...(currentTask.subtasks || [])];
                                        newSubtasks.push({ id: '', name: '', is_completed: false });
                                        setCurrentTask({ ...currentTask, subtasks: newSubtasks });
                                    }}
                                >
                                    Add Subtask
                                </Button>
                            </Box>
                            {currentTask.subtasks && currentTask.subtasks.map((st, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                    <Checkbox 
                                        checked={st.is_completed}
                                        onChange={(e) => {
                                            const newSubtasks = [...currentTask.subtasks];
                                            newSubtasks[index].is_completed = e.target.checked;
                                            setCurrentTask({ ...currentTask, subtasks: newSubtasks });
                                        }}
                                    />
                                    <TextField 
                                        fullWidth size="small" placeholder="Subtask description" 
                                        value={st.name}
                                        onChange={(e) => {
                                            const newSubtasks = [...currentTask.subtasks];
                                            newSubtasks[index].name = e.target.value;
                                            setCurrentTask({ ...currentTask, subtasks: newSubtasks });
                                        }}
                                        sx={{ textDecoration: st.is_completed ? 'line-through' : 'none' }}
                                    />
                                    <IconButton 
                                        size="small" color="error" 
                                        onClick={() => {
                                            const newSubtasks = [...currentTask.subtasks];
                                            newSubtasks.splice(index, 1);
                                            setCurrentTask({ ...currentTask, subtasks: newSubtasks });
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            {(!currentTask.subtasks || currentTask.subtasks.length === 0) && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No subtasks added yet.</Typography>
                            )}
                        </Paper>

                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleClose} sx={{ borderRadius: 2, fontWeight: '600', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1.5, boxShadow: '0 4px 14px rgba(30, 60, 114, 0.2)' }}>
                        {editMode ? 'Save Changes' : 'Create Task'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyTasks;